import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, Button, Box, LinearProgress, Snackbar } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * Session Warning Component
 * 
 * Displays warnings about session expiration and provides options to:
 * - Extend the session
 * - Dismiss the warning
 * - See countdown timer
 */
const SessionWarning: React.FC = () => {
  const { sessionWarning, dismissSessionWarning, refreshToken } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (sessionWarning.show) {
      // Start countdown
      setCountdown(sessionWarning.minutesRemaining * 60); // Convert to seconds
      
      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [sessionWarning.show, sessionWarning.minutesRemaining]);

  const handleExtendSession = async () => {
    setIsRefreshing(true);
    try {
      const success = await refreshToken();
      if (success) {
        dismissSessionWarning();
        // Show success notification
        console.log('Session extended successfully');
      } else {
        console.error('Failed to extend session');
      }
    } catch (error) {
      console.error('Error extending session:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatCountdown = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progressValue = sessionWarning.minutesRemaining > 0 
    ? (countdown / (sessionWarning.minutesRemaining * 60)) * 100 
    : 0;

  // Don't render if warning is not shown
  if (!sessionWarning.show) {
    return null;
  }

  return (
    <Snackbar
      open={sessionWarning.show}
      onClose={dismissSessionWarning}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ 
        marginTop: '64px', // Account for header
        maxWidth: '500px',
      }}
    >
      <Alert
        severity="warning"
        icon={<WarningIcon />}
        onClose={dismissSessionWarning}
        sx={{ 
          width: '100%',
          boxShadow: 3,
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
      >
        <AlertTitle>
          <strong>Session Expiring Soon</strong>
        </AlertTitle>
        
        <Box sx={{ mb: 2 }}>
          Your session will expire in <strong>{formatCountdown(countdown)}</strong>.
          Please save your work and extend your session to continue.
        </Box>

        <LinearProgress 
          variant="determinate" 
          value={progressValue}
          sx={{ 
            mb: 2,
            height: 6,
            borderRadius: 1,
            backgroundColor: 'rgba(251, 140, 0, 0.2)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#fb8c00'
            }
          }}
        />

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={dismissSessionWarning}
            disabled={isRefreshing}
          >
            Dismiss
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleExtendSession}
            disabled={isRefreshing}
            sx={{ 
              backgroundColor: '#fb8c00',
              '&:hover': {
                backgroundColor: '#f57c00'
              }
            }}
          >
            {isRefreshing ? 'Extending...' : 'Extend Session'}
          </Button>
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default SessionWarning;